import React, { useRef, useEffect, useState } from 'react';

const Contact = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    interest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage({ type: '', text: '' });
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${apiUrl}/kbis/demo-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          message: `Interest: ${formData.interest}\n\n${formData.message}`
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit form');
      }
      
      const data = await response.json();
      setSubmitMessage({ 
        type: 'success', 
        text: 'Thank you! Your message has been sent successfully. We\'ll get back to you soon.' 
      });
      setFormData({
        name: '',
        email: '',
        company: '',
        interest: '',
        message: ''
      });
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSubmitMessage({ type: '', text: '' });
      }, 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitMessage({ 
        type: 'error', 
        text: 'Failed to submit your message. Please try again or email us directly at support@thoughtparadigm.com' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    {
      title: 'Email Us',
      value: 'support@thoughtparadigm.com',
      action: 'mailto:support@thoughtparadigm.com',
      description: 'Get in touch for general inquiries'
    },
    {
      title: 'Sales Inquiries',
      value: 'sales@thoughtparadigm.com',
      action: 'mailto:sales@thoughtparadigm.com',
      description: 'Discuss enterprise solutions'
    },
    {
      title: 'Research Partnership',
      value: 'research@thoughtparadigm.com',
      action: 'mailto:research@thoughtparadigm.com',
      description: 'Collaborate on cutting-edge AI'
    }
  ];

  const opportunities = [
    {
      title: 'Strategic Collaborations',
      description: 'Partner with us to build the future of intelligent enterprise systems'
    },
    {
      title: 'Pilot Opportunities',
      description: 'Be among the first to experience KnowTrace in your organization'
    },
    {
      title: 'Talent Partnerships',
      description: 'Join our team of world-class engineers and researchers'
    },
    {
      title: 'Investment Opportunities',
      description: 'Invest in the next generation of enterprise AI technology'
    }
  ];

  return (
    <section id="contact" className="contact" ref={sectionRef}>
      <div className="container">
        <div className={`section-content ${isVisible ? 'section-content--visible' : ''}`}>
          <div className="contact-hero">
            <div className="section-badge">
              <span className="badge-text">Get in Touch</span>
            </div>
            <h2 className="contact-title">
              Let's Build the Future of <span className="title-accent">Intelligent Work</span> Together
            </h2>
            <p className="contact-subtitle">
              Whether you're looking to transform your organization's knowledge management 
              or explore partnership opportunities, we'd love to hear from you.
            </p>
          </div>
          
          <div className="contact-main">
            <div className="contact-form-section">
              <div className="form-header">
                <h3 className="form-title">Start the Conversation</h3>
                <p className="form-description">
                  Tell us about your challenge, and we'll show you how KnowTrace can help.
                </p>
              </div>
              
              {submitMessage.text && (
                <div className={`form-message form-message--${submitMessage.type}`}>
                  {submitMessage.text}
                </div>
              )}
              
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Your full name"
                    disabled={isSubmitting}
                    required
                  />
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="your@company.com"
                      disabled={isSubmitting}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="company" className="form-label">Company</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Your organization"
                      disabled={isSubmitting}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="interest" className="form-label">I'm interested in</label>
                  <select
                    id="interest"
                    name="interest"
                    value={formData.interest}
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={isSubmitting}
                    required
                  >
                    <option value="">Select your interest</option>
                    <option value="knowtrace-demo">KnowTrace Demo</option>
                    <option value="enterprise-pilot">Enterprise Pilot Program</option>
                    <option value="partnership">Strategic Partnership</option>
                    <option value="research">Research Collaboration</option>
                    <option value="investment">Investment Opportunity</option>
                    <option value="careers">Career Opportunities</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="message" className="form-label">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className="form-textarea"
                    placeholder="Tell us about your needs, challenges, or how we can work together..."
                    rows={4}
                    disabled={isSubmitting}
                    required
                  />
                </div>
                
                <button type="submit" className="form-submit" disabled={isSubmitting}>
                  <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                  <svg className="submit-arrow" viewBox="0 0 20 20">
                    <path d="M7 3l7 7-7 7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </form>
            </div>
            
            <div className="contact-info-section">
              <div className="contact-methods">
                <h3 className="methods-title">Reach Out Directly</h3>
                {contactMethods.map((method, index) => (
                  <a key={index} href={method.action} className="method-card">
                    <div className="method-content">
                      <h4 className="method-title">{method.title}</h4>
                      <div className="method-value">{method.value}</div>
                      <p className="method-description">{method.description}</p>
                    </div>
                  </a>
                ))}
              </div>
              
              <div className="opportunities-section">
                <h3 className="opportunities-title">Partnership Opportunities</h3>
                <div className="opportunities-grid">
                  {opportunities.map((opportunity, index) => (
                    <div key={index} className="opportunity-card">
                      <h4 className="opportunity-title">{opportunity.title}</h4>
                      <p className="opportunity-description">{opportunity.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
